import React, { createContext, useContext, useState, useEffect } from 'react';

export const FORCE_DESKTOP_VIEW_STORAGE_KEY = 'ranger_force_desktop_view';

const MobileContext = createContext({
  isMobile: false,
  screenWidth: 0,
  isMobileDevice: false,
  forceDesktopView: false,
  setForceDesktopView: () => {},
});

// Mobile breakpoint - phones are under 768px width
const MOBILE_BREAKPOINT = 768;

// Check if running as standalone PWA
function isStandalonePWA() {
  if (typeof window === 'undefined') return false;
  
  // Check display-mode media query (most reliable)
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  
  // iOS Safari standalone mode
  const isIOSStandalone = window.navigator.standalone === true;
  
  // Android TWA (Trusted Web Activity)
  const isTWA = document.referrer.includes('android-app://');
  
  return isStandalone || isIOSStandalone || isTWA;
}

// Check if device is likely a mobile/touch device
function detectMobileDevice() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  
  // Check user agent for mobile indicators (comprehensive list)
  const userAgent = (navigator.userAgent || navigator.vendor || window.opera || '').toLowerCase();
  
  // Common mobile device identifiers
  const mobileKeywords = [
    'android',
    'webos', 
    'iphone',
    'ipad',
    'ipod',
    'blackberry',
    'iemobile',
    'opera mini',
    'opera mobi',
    'mobile',
    'tablet',
    'samsung',
    'galaxy',
    'pixel',
    'nexus',
    'lg-',
    'htc',
    'nokia',
    'windows phone',
    'silk',
    'kindle',
    'playbook',
    'bb10',
  ];
  
  const isMobileUA = mobileKeywords.some(keyword => userAgent.includes(keyword));
  
  // Check for touch capability
  const hasTouch = 'ontouchstart' in window || 
                   navigator.maxTouchPoints > 0 || 
                   navigator.msMaxTouchPoints > 0;
  
  // Check screen width
  const isNarrowScreen = window.innerWidth < MOBILE_BREAKPOINT;
  
  // Check if screen is portrait oriented (common for phones)
  const isPortrait = window.innerHeight > window.innerWidth;
  
  // Check if running as PWA
  const isPWA = isStandalonePWA();
  
  // Check device pixel ratio (phones typically have higher pixel ratios)
  const hasHighDPR = window.devicePixelRatio >= 2;
  
  // Check screen dimensions (actual screen, not viewport)
  const screenWidth = window.screen?.width || window.innerWidth;
  const screenHeight = window.screen?.height || window.innerHeight;
  const isSmallScreen = Math.min(screenWidth, screenHeight) < 500;
  
  // Consider it mobile if:
  return isMobileUA || isNarrowScreen || (hasTouch && isPortrait && window.innerWidth < 600) || (isPWA && hasTouch) || (hasTouch && isSmallScreen);
}

function getInitialForceDesktopView() {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.localStorage.getItem(FORCE_DESKTOP_VIEW_STORAGE_KEY) === 'true';
}

export function MobileProvider({ children }) {
  const [screenWidth, setScreenWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  const [isMobileDevice, setIsMobileDevice] = useState(() => detectMobileDevice());
  const [forceDesktopView, setForceDesktopView] = useState(() => getInitialForceDesktopView());

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let resizeTimer;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        setScreenWidth(window.innerWidth);
        // Re-check mobile detection on resize
        setIsMobileDevice(detectMobileDevice());
      }, 150);
    };

    // Set initial value
    setScreenWidth(window.innerWidth);
    setIsMobileDevice(detectMobileDevice());

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(FORCE_DESKTOP_VIEW_STORAGE_KEY, forceDesktopView ? 'true' : 'false');
  }, [forceDesktopView]);

  // Force mobile mode if screen is narrow OR if detected as mobile device
  const isMobile = !forceDesktopView && (screenWidth < MOBILE_BREAKPOINT || isMobileDevice);

  return (
    <MobileContext.Provider value={{ isMobile, screenWidth, isMobileDevice, forceDesktopView, setForceDesktopView }}>
      {children}
    </MobileContext.Provider>
  );
}

export function useMobile() {
  return useContext(MobileContext);
}

export default MobileContext;
